<script setup lang="ts">
import DestructiveConfirmDialog from '~/components/app/DestructiveConfirmDialog.vue'
import DiscardUnsavedDialog from '~/components/app/DiscardUnsavedDialog.vue'
import OrganizationConflictDialog from '~/components/Forms/OrganizationConflictDialog.vue'
import CreateEquipeContactsTable from '~/components/Forms/Equipe/CreateEquipeContactsTable.vue'
import EntityFormFooter from '~/components/Forms/EntityFormFooter.vue'
import SourceInput from '~/components/ui/SourceInput.vue'
import SourceRichTextEditor from '~/components/ui/SourceRichTextEditor.vue'
import { useCreateEquipeForm } from '~/composables/equipe/useCreateEquipeForm'

type Props = {
  showOverlay?: boolean
  directionName: string
  domaineName: string
  agenceName: string
  /** Identifiant équipe à modifier (format dirLib~domLib~ageLib~equLib) ou équipe de direction type~type~type~type */
  equipeToEdit?: string
  isEditing?: boolean
  /** Pour une équipe de direction : nom fixe (dirLib=domLib=ageLib=equLib). Si défini, le champ nom n’est pas affiché. */
  defaultEquLib?: string
}

const props = withDefaults(defineProps<Props>(), {
  showOverlay: false,
  isEditing: false,
  equipeToEdit: '',
  defaultEquLib: ''
})

const emit = defineEmits<{
  (event: 'close' | 'refetch'): void
  (
    event: 'created' | 'deleted',
    payload: { dirLib: string; domLib: string; ageLib: string; equLib: string },
  ): void
}>()

const hasUnsavedChanges = defineModel<boolean>('hasUnsavedChanges', { default: false })

const {
  mayDeleteEquipe,
  feedback,
  loading,
  loadDone,
  showDeleteConfirm,
  contactsTableRef,
  equipe,
  equipeContextLabel,
  hideEquipeNameField,
  showDiscardConfirm,
  requestClose,
  confirmDiscard,
  cancelDiscard,
  validateForm,
  onConfirmDelete,
  concurrent,
} = useCreateEquipeForm(props, emit, hasUnsavedChanges)

function confirmConflict(): void {
  concurrent.acceptChoices(equipe.value)
  void validateForm()
}

function restoreFromTrash(): void {
  concurrent.closeConflict()
  void navigateTo('/admin/organigram-trash')
}
</script>

<template>
  <div class="form-content" :class="{ 'form-content--inline': !showOverlay }">
    <!-- Skeleton en overlay pendant le chargement (évite v-if/v-else qui empêchait l'affichage) -->
    <div
      v-if="isEditing && !loadDone"
      class="form-skeleton-overlay"
      aria-busy="true"
      aria-label="Chargement de l'équipe"
    >
      <div class="form-skeleton">
        <div class="skeleton-line skeleton-title" />
        <div class="skeleton-content">
          <div v-if="!hideEquipeNameField" class="skeleton-line skeleton-input" />
          <div class="skeleton-line skeleton-textarea" />
        </div>
        <div class="section-field">
          <p class="h6">Informations de contact</p>
          <table class="source-table source-table--skeleton">
            <thead>
              <tr>
                <th>Intitulé</th>
                <th>Contact</th>
                <th class="action-col">Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in 3" :key="i">
                <td><div class="skeleton-line skeleton-cell" /></td>
                <td><div class="skeleton-line skeleton-cell" /></td>
                <td class="action-cell"><div class="skeleton-line skeleton-cell-action" /></td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="h6">Les missions de l'équipe</p>
        <div class="skeleton-line skeleton-textarea" />
        <div class="skeleton-footer">
          <div class="skeleton-line skeleton-btn" />
          <div class="skeleton-line skeleton-btn" />
        </div>
      </div>
    </div>

    <div class="form-body">
    <p class="info-text info-text--secondary">
      {{ isEditing ? equipeContextLabel.modification : equipeContextLabel.creation }}
      <span class="highlight">{{ equipeContextLabel.name }}</span>.
    </p>

    <div class="content">
      <SourceInput
          v-if="!hideEquipeNameField"
          :model-value="equipe.equLib"
          name="equLib"
          label="Nom de l'équipe *"
          placeholder="Entrez le nom de l'équipe"
          size="medium"
          type="text"
          :error="feedback.fieldError('equLib')"
          @update:model-value="(v) => { equipe.equLib = v; feedback.clearFieldError('equLib') }"
      />

      <SourceRichTextEditor
          :model-value="equipe.equDesc"
          name="equDesc"
          label="Description de l'équipe"
          placeholder="..."
          size="medium"
          :error="feedback.fieldError('equDesc')"
          @update:model-value="(v) => { equipe.equDesc = v; feedback.clearFieldError('equDesc') }"
      />

      <CreateEquipeContactsTable
        ref="contactsTableRef"
        v-model:rows="equipe.contacts"
        :loading="loading"
      />

      <p class="h6">Les missions de l'équipe</p>
      <SourceRichTextEditor
          :model-value="equipe.equMis"
          name="equMis"
          label="Ajouter les missions de l'équipe"
          placeholder="..."
          size="medium"
          :error="feedback.fieldError('equMis')"
          @update:model-value="(v) => { equipe.equMis = v; feedback.clearFieldError('equMis') }"
      />
    </div>
    </div>

    <EntityFormFooter
      :show-delete="isEditing && mayDeleteEquipe"
      :delete-disabled="loading || !loadDone"
      :cancel-disabled="loading"
      :submit-disabled="loading || (isEditing && !loadDone)"
      :submit-label="isEditing ? 'Enregistrer' : 'Créer'"
      @delete="showDeleteConfirm = true"
      @cancel="requestClose"
      @submit="validateForm"
    />

    <DestructiveConfirmDialog
      v-model:open="showDeleteConfirm"
      title="Supprimer l'équipe"
      :description="`Êtes-vous sûr de vouloir supprimer l'équipe « ${equipe.equLib} » ? Cette action est irréversible.`"
      :confirm-phrase="equipe.equLib"
      :loading="loading"
      @confirm="onConfirmDelete"
    />

    <OrganizationConflictDialog
      :open="concurrent.conflict.value !== null || concurrent.deletedOpen.value"
      :deleted="concurrent.deletedOpen.value"
      :fields="concurrent.conflict.value?.fields ?? []"
      :choices="concurrent.choices.value"
      @choose="(field, choice) => { concurrent.choices.value[field] = choice }"
      @confirm="confirmConflict"
      @close="concurrent.closeConflict()"
      @restore="restoreFromTrash"
    />

    <DiscardUnsavedDialog
      :show="showDiscardConfirm"
      @close="cancelDiscard"
      @confirm="confirmDiscard"
    />
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/components/entity-form" as *;
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/index" as *;
@use "~/assets/scss/app" as *;

.form-content--inline {
  min-height: auto;
  overflow: visible;
  padding: 0;
  background-color: transparent;
}

.form-content--inline .form-body {
  overflow: visible;
  flex: none;
  min-height: auto;
}

.form-content--inline .footer {
  margin-inline: 0;
  border-top: none;
  padding: $spacing-m 0 0;
  background: transparent;
}

.info-text {
  margin-top: 2px;
  margin-bottom: 0;
}

.info-text--secondary {
  font-size: 0.9375rem;
  color: c.$enedis-neutral-300;
}

.section-field {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 8px;
}

.h6 {
  margin: 0;
  color: c.$enedis-primary-500;
  @extend .h6;
}

.highlight {
  color: c.$enedis-neutral-400;
  font-weight: normal;
}

.content {
  justify-content: flex-start;
}

.footer {
  flex-wrap: wrap;
  gap: $spacing-m;
}

.form-skeleton-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: c.$enedis-neutral-10;
  padding: 8px;
  overflow-y: auto;
}

.form-skeleton .h6 {
  margin: 0;
  color: c.$enedis-primary-500;
  @extend .h6;
}

/* Tableau skeleton : header visible, lignes sans valeur */
.source-table--skeleton {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.source-table--skeleton th,
.source-table--skeleton td {
  padding: 8px 12px;
  border: 1px solid var(--color-border-input);
}

.source-table--skeleton th {
  background-color: var(--color-form-section-bg);
  font-weight: 600;
  text-align: left;
}

.source-table--skeleton .action-col {
  width: 140px;
  text-align: center;
}

.source-table--skeleton .action-cell {
  text-align: center;
}

.skeleton-cell {
  height: 24px;
  min-width: 80px;
}

.skeleton-cell-action {
  height: 24px;
  width: 48px;
  margin: 0 auto;
}

.undo-banner {
  position: fixed;
  left: 50%;
  bottom: 16px;
  transform: translateX(-50%);
  z-index: 13000;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: $spacing-xs $spacing-xs;
  border-radius: 8px;
  background: var(--color-bg-elevated);
  box-shadow: var(--color-shadow-panel);
  border: 1px solid c.$enedis-neutral-60;
  width: min(560px, calc(100vw - 24px));
}

.undo-banner__text {
  font-size: 14px;
  color: var(--color-text);
}
</style>
